export type Provider = 'openai' | 'gemini' | 'claude';

export interface ProviderSettings {
  apiKey: string;
  model: string;
  models?: string[];
  modelsFetchedAt?: number;
  modelsSource?: 'live' | 'fallback';
}

export interface Settings {
  llm: { activeProvider: Provider; providers: Partial<Record<Provider, ProviderSettings>> };
  /** What she calls it and how it addresses her; both chosen on first launch. */
  companionName: string;
  userName: string;
  firstMetAt?: number;
  /** Extraction runs after every exchange; off means it only talks and never learns (useful for A/B). */
  memoryEnabled: boolean;
  /** When it made the one-time offer "tell me about your life in one go"; never repeated. */
  invitedAt?: number;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  at: number;
  /** Which provider/model produced an assistant message — the experiment variable. */
  model?: string;
  error?: string;
}

export type MemoryKind = 'about_me' | 'person' | 'event' | 'feeling' | 'preference' | 'belief' | 'prediction' | 'followup';

/** What a companion should come to know about a person; the gaps drive what it gets curious about. */
export type ProfileTopic = 'people' | 'family' | 'comfort' | 'triggers' | 'stress' | 'work' | 'ongoing' | 'daily' | 'hobbies' | 'health' | 'past';

export const MEMORY_KINDS: MemoryKind[] = ['about_me', 'person', 'event', 'feeling', 'preference', 'belief', 'prediction', 'followup'];

export interface Memory {
  id: string;
  kind: MemoryKind;
  /** One sentence, third person, relative dates already resolved to absolute ones. */
  text: string;
  /** People / things it is about, in canonical form (婆婆, 小王, 老板); used for retrieval. */
  subjects: string[];
  /** Which part of her picture this note fills in. */
  topic?: ProfileTopic;
  importance: 1 | 2 | 3 | 4 | 5;
  createdAt: number;
  updatedAt: number;
  /** YYYY-MM-DD the thing happened, or for a prediction the day she said it. */
  happenedAt?: string;
  sourceIds: string[];
  /** followup / prediction: still pending, or already raised / borne out. */
  status?: 'open' | 'done';
  lastRecalledAt?: number;
}

/** One line per day: what she talked about and how she was. */
export interface JournalEntry {
  date: string; // YYYY-MM-DD
  text: string;
  updatedAt: number;
}
